import React, { useRef, useEffect, useState } from 'react';
import { AtSign, Building, Send, User, Mail, MessageSquare, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Contact: React.FC = () => {
  const contactRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('marketing');
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'name':
        return value.length < 2 ? 'Name must be at least 2 characters' : '';
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address' : '';
      case 'message':
        return value.length < 10 ? 'Message must be at least 10 characters' : '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
    setFocusedField(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const newErrors: { [key: string]: string } = {};
    Object.entries(formState).forEach(([key, value]) => {
      if (key !== 'company') { // company is optional
        const error = validateField(key, value);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormState({ name: '', email: '', company: '', message: '' });

    // Reset success state after 3 seconds
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = contactRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => observer.observe(el));

    return () => {
      elements?.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <section className="section-padding bg-dark-bg-secondary relative" id="contact" ref={contactRef}>


      <div className="container-custom relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll opacity-0 transition-opacity duration-1000">
              <h2 className="text-4xl md:text-5xl font-bold text-light-text-primary mb-6">
                {t('contact.ready_to_transform_customer_experience')}
              </h2>
              <p className="text-xl text-light-text-muted mb-8 leading-relaxed">
                {t('contact.get_in_touch_with_our_team')}
              </p>

              <div className="space-y-6">
                <div className="card-glow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-n8n-green/20 to-n8n-blue/20 rounded-2xl">
                      <MessageSquare className="text-n8n-green" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-light-text-primary">{t('contact.schedule_a_demo')}</h3>
                  </div>
                  <p className="text-light-text-muted mb-6">
                    {t('contact.see_humanreplica_in_action')}
                  </p>
                  <button className="btn-secondary">
                    {t('contact.book_demo_session')}
                  </button>
                </div>

                <div className="card-glow">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-n8n-purple/20 to-n8n-blue/20 rounded-2xl">
                      <User className="text-n8n-purple" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-light-text-primary">{t('contact.start_your_free_trial')}</h3>
                  </div>
                  <p className="text-light-text-muted mb-6">
                    {t('contact.experience_full_capabilities')}
                  </p>
                  <button className="btn-primary">
                    {t('contact.start_free_trial')}
                  </button>
                </div>
              </div>
            </div>

            <div className="card-glow animate-on-scroll opacity-0 transition-opacity duration-1000 delay-300">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold text-light-text-primary">{t('contact.contact_us_title')}</h3>
                {isSubmitted && (
                  <div className="flex items-center text-green-400 animate-slide-up">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">Message sent!</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-light-text-secondary mb-2">
                    {t('contact.full_name')} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-300 ${focusedField === 'name' ? 'text-n8n-purple' : 'text-light-text-muted'
                      }`}>
                      <User size={18} />
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={handleBlur}
                      className={`input-field pl-12 transition-all duration-300 ${focusedField === 'name' ? 'border-n8n-purple shadow-glow' : ''
                        } ${errors.name ? 'border-red-500' : ''}`}
                      placeholder={t('contact.your_name')}
                      required
                    />
                    {errors.name && (
                      <p className="text-red-400 text-sm mt-1 animate-slide-up">{errors.name}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-light-text-secondary mb-2">
                    {t('contact.email_address')} *
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-300 ${focusedField === 'email' ? 'text-n8n-purple' : 'text-light-text-muted'
                      }`}>
                      <Mail size={18} />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={handleBlur}
                      className={`input-field pl-12 transition-all duration-300 ${focusedField === 'email' ? 'border-n8n-purple shadow-glow' : ''
                        } ${errors.email ? 'border-red-500' : ''}`}
                      placeholder={t('contact.your_email')}
                      required
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1 animate-slide-up">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-light-text-secondary mb-2">
                    {t('contact.company')} (optional)
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-300 ${focusedField === 'company' ? 'text-n8n-purple' : 'text-light-text-muted'
                      }`}>
                      <Building size={18} />
                    </div>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formState.company}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('company')}
                      onBlur={handleBlur}
                      className={`input-field pl-12 transition-all duration-300 ${focusedField === 'company' ? 'border-n8n-purple shadow-glow' : ''
                        }`}
                      placeholder={t('contact.your_company')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-light-text-secondary mb-2">
                    {t('contact.message')} *
                  </label>
                  <div className="relative">
                    <div className={`absolute top-4 left-4 pointer-events-none transition-colors duration-300 ${focusedField === 'message' ? 'text-n8n-purple' : 'text-light-text-muted'
                      }`}>
                      <MessageSquare size={18} />
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={handleBlur}
                      className={`input-field pl-12 min-h-[120px] transition-all duration-300 ${focusedField === 'message' ? 'border-n8n-purple shadow-glow' : ''
                        } ${errors.message ? 'border-red-500' : ''}`}
                      placeholder={t('contact.your_message')}
                      rows={4}
                      required
                    />
                    {errors.message && (
                      <p className="text-red-400 text-sm mt-1 animate-slide-up">{errors.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting || isSubmitted}
                    className={`btn-primary w-full flex items-center justify-center gap-3 transition-all duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'
                      } ${isSubmitted ? 'bg-green-600 border-green-600' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : isSubmitted ? (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        Message Sent!
                      </>
                    ) : (
                      <>
                        {t('contact.send_message')}
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;